import { t } from '@lingui/macro';
import { IMapFeatureData } from './IMapData';
import { CorrelationListItemModel } from './ICorrelation';
import { ILnSDetails } from './ILnSResponse';
import { PaginatedResponse } from '@hwm/ui-lib';

export interface SurveyMapModel {
  id: string;
  latitude: number;
  longitude: number;
}

//

export interface SurveysListModel extends PaginatedResponse<SurveyModel> {}

export interface SurveyModel extends SurveyMapModel {
  name: string;
  surveyor: string;
  date: string; // as Date
  duration: string;
  status: SurveyStatusType;
  result: SurveyResultType;
  numberOfRecordings: number;
  numberOfCorrelations: number;
  coordinatesType: SurveyCoordinatesType;
  numberOfSites: number;
  linkedSurveys: LinkedSurveyModel[] | null;
}

const SurveyStatus = ['Configuring', 'DevicesConfigured', 'MapLayoutCreated', 'DataCollected', 'Completed'] as const;
export type SurveyStatusType = (typeof SurveyStatus)[number];
export const SurveyStatusOptionsFn = (): Record<SurveyStatusType, string> => ({
  Configuring: t`Configuring`,
  DevicesConfigured: t`Devices Configured`,
  MapLayoutCreated: t`Map Layout Created`,
  DataCollected: t`Data Collected`,
  Completed: t`Completed`,
});

const SurveyResult = ['NotSet', 'LeakDetected', 'NoLeak', 'Inconclusive'] as const;
export type SurveyResultType = (typeof SurveyResult)[number];
export const SurveyResultOptionsFn = (): Record<SurveyResultType, string> => ({
  NotSet: t`Not Set`,
  LeakDetected: t`Leak Detected`,
  NoLeak: t`No Leak`,
  Inconclusive: t`Inconclusive`,
});

const SurveyCoordinates = ['None', 'Basic', 'MapOnly', 'MapAndGIS'] as const;
export type SurveyCoordinatesType = (typeof SurveyCoordinates)[number];
export const SurveyCoordinatesOptionsFn = (): Record<SurveyCoordinatesType, string> => ({
  None: t`None`,
  Basic: t`Basic`,
  MapOnly: t`Map Only`,
  MapAndGIS: t`Map and GIS`,
});

interface LinkedSurveyModel {
  id: string;
  date: string;
}

//

export interface SurveyDetailsCloneRequestModel {
  id: string;
  data: SurveyDetailsCloneDataModel[];
}

export interface SurveyDetailsCloneDataModel {
  id: number;
  latitude: number;
  longitude: number;
}

//

export interface SurveyDetailsModel extends SurveyModel {
  devices: SurveyDetailsDeviceModel[];
  mapData: IMapFeatureData[];
  recordings: SurveyDetailsRecordingModel[];
  latestCorrelations: SurveyDetailsCorrelationModel[];
  lns: SurveyDetailsLnsModel[];
  notes: SurveyDetailsNoteModel[];
}

export interface SurveyDetailsDeviceModel {
  serialNumber: string;
  loggerType: string;
  recordingSettings: {
    filterNotch: number;
    filterLow: number;
    filterHigh: number;
  };
}

export interface SurveyDetailsNoteModel {
  noteId: string;
  time: string; // as Date
  note: string;
  url: string;
}

export interface SurveyDetailsRecordingModel {
  id: string;
  deviceId: string;
  date: string; // as Date
  url: string;
}

type CorrelationListItemModelToOmitType = keyof Pick<CorrelationListItemModel, 'id' | 'isSurveyMode'>;
export interface SurveyDetailsCorrelationModel
  extends Omit<CorrelationListItemModel, CorrelationListItemModelToOmitType> {
  id: string;
  deviceId1: string;
  deviceId2: string;
  leakLatitude: number;
  leakLongitude: number;
}

export interface SurveyDetailsLnsModel extends ILnSDetails {}
